import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Star, Sparkles } from "lucide-react";

const COLORS = ["#818cf8", "#34d399", "#fbbf24", "#f472b6", "#22d3ee", "#a78bfa"];

function Confetti() {
  const pieces = Array.from({ length: 40 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.3;
        const dur = 1.6 + Math.random() * 1.2;
        const size = 6 + Math.random() * 8;
        const color = COLORS[i % COLORS.length];
        return (
          <motion.span
            key={i}
            initial={{ y: -40, x: 0, opacity: 1, rotate: 0 }}
            animate={{ y: "110vh", x: (Math.random() - 0.5) * 200, rotate: Math.random() * 720, opacity: [1, 1, 0] }}
            transition={{ duration: dur, delay, ease: "easeIn" }}
            style={{ position: "absolute", top: 0, left: `${left}%`, width: size, height: size * 1.4, background: color, borderRadius: 2 }}
          />
        );
      })}
    </div>
  );
}

export default function CelebrationOverlay() {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    let timer;
    const handler = (e) => {
      setEvent(e.detail);
      clearTimeout(timer);
      timer = setTimeout(() => setEvent(null), 3200);
    };
    window.addEventListener("lp:celebrate", handler);
    return () => { window.removeEventListener("lp:celebrate", handler); clearTimeout(timer); };
  }, []);

  const Icon = event?.type === "level" ? Star : Trophy;
  const gradient = event?.type === "level" ? "from-indigo-500 to-violet-600" : "from-yellow-400 to-amber-500";

  return (
    <AnimatePresence>
      {event && (
        <motion.div
          data-testid="celebration-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setEvent(null)}
          className="fixed inset-0 z-[100] grid place-items-center bg-black/60 backdrop-blur-sm px-6"
        >
          <Confetti />
          <motion.div
            initial={{ scale: 0.6, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="relative glass rounded-3xl p-8 text-center max-w-xs w-full border border-white/15"
          >
            <motion.div
              initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", delay: 0.1, stiffness: 200 }}
              className={`h-24 w-24 rounded-3xl bg-gradient-to-br ${gradient} grid place-items-center mx-auto mb-5 shadow-2xl`}
            >
              <Icon size={48} className="text-white" fill="currentColor" />
            </motion.div>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground flex items-center justify-center gap-1.5">
              <Sparkles size={12} className="text-yellow-400" /> {event.type === "level" ? "Level Up" : "Achievement"}
            </span>
            <h2 className="font-display font-extrabold text-3xl tracking-tight mt-1.5">{event.title}</h2>
            {event.subtitle && <p className="text-muted-foreground text-sm mt-1">{event.subtitle}</p>}
            <p className="text-[11px] text-muted-foreground mt-5">Tap to continue</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
